import { useState, useEffect } from "react";
import { useContent } from "@/context/ContentContext";
import { Save, Plus, Trash2, Calendar } from "lucide-react";

export default function ScheduleManager() {
  const { content, updateSection } = useContent();
  const [schedule, setSchedule] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeDay, setActiveDay] = useState(0);

  useEffect(() => {
    if (content.schedule) setSchedule(JSON.parse(JSON.stringify(content.schedule)));
  }, [content.schedule]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSection("schedule", schedule);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const updateWeek = (i, field, value) => {
    const semanas = [...(schedule.semanas || [])];
    semanas[i] = { ...semanas[i], [field]: value };
    setSchedule(p => ({ ...p, semanas }));
  };

  const updateSlot = (dayIdx, slotIdx, field, value) => {
    const dias = [...(schedule.dias || [])];
    const actividades = [...(dias[dayIdx].actividades || [])];
    actividades[slotIdx] = { ...actividades[slotIdx], [field]: value };
    dias[dayIdx] = { ...dias[dayIdx], actividades };
    setSchedule(p => ({ ...p, dias }));
  };

  const addSlot = (dayIdx) => {
    const dias = [...(schedule.dias || [])];
    dias[dayIdx] = { ...dias[dayIdx], actividades: [...(dias[dayIdx].actividades || []), { hora: "", g1: "", g2: "" }] };
    setSchedule(p => ({ ...p, dias }));
  };

  const removeSlot = (dayIdx, slotIdx) => {
    const dias = [...(schedule.dias || [])];
    const actividades = dias[dayIdx].actividades.filter((_, i) => i !== slotIdx);
    dias[dayIdx] = { ...dias[dayIdx], actividades };
    setSchedule(p => ({ ...p, dias }));
  };

  const dias = schedule.dias || [];

  return (
    <div data-testid="schedule-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-['Montserrat'] font-bold text-2xl text-[#0F172A] mb-1">Horarios y Calendario</h1>
          <p className="text-[#475569] font-['Montserrat'] text-sm">Gestiona las fechas y el cronograma semanal</p>
        </div>
        <button onClick={handleSave} disabled={saving} data-testid="save-schedule-btn"
          className={`flex items-center gap-2 px-5 py-2 rounded-lg font-['Montserrat'] font-semibold text-sm transition-all ${saved ? "bg-green-500 text-white" : "bg-[#0072A4] text-white hover:bg-[#005A80]"} disabled:opacity-60`}>
          <Save size={15} /> {saving ? "Guardando..." : saved ? "¡Guardado!" : "Guardar"}
        </button>
      </div>

      {/* Section header */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 mb-6">
        <h3 className="font-['Montserrat'] font-semibold text-[#0F172A] mb-4">Encabezado de la sección</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Etiqueta (pill)</label>
            <input value={schedule.section_label || ""} onChange={e => setSchedule(p => ({ ...p, section_label: e.target.value }))} data-testid="schedule-section-label"
              className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]" placeholder="Calendario y Horarios" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Título principal</label>
            <input value={schedule.title || ""} onChange={e => setSchedule(p => ({ ...p, title: e.target.value }))} data-testid="schedule-title-input"
              className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]" placeholder="¿Cuándo y cómo?" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Subtítulo</label>
            <input value={schedule.subtitle || ""} onChange={e => setSchedule(p => ({ ...p, subtitle: e.target.value }))} data-testid="schedule-subtitle-input"
              className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]" placeholder="El campamento se desarrolla en dos semanas de pura aventura..." />
          </div>
        </div>
      </div>

      {/* General hours */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 mb-6">
        <h3 className="font-['Montserrat'] font-semibold text-[#0F172A] mb-4">Horarios Generales</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Horario normal</label>
            <input value={schedule.horario_normal || ""} onChange={e => setSchedule(p => ({ ...p, horario_normal: e.target.value }))} data-testid="horario-normal"
              className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]" placeholder="9:00 - 14:00" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Horario madrugadores</label>
            <input value={schedule.horario_madrugadores || ""} onChange={e => setSchedule(p => ({ ...p, horario_madrugadores: e.target.value }))} data-testid="horario-madrugadores"
              className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]" placeholder="8:00 - 15:00" />
          </div>
        </div>
      </div>

      {/* Weeks */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 mb-6">
        <h3 className="font-['Montserrat'] font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
          <Calendar size={18} color="#0072A4" /> Semanas
        </h3>
        <div className="space-y-4">
          {(schedule.semanas || []).map((semana, i) => (
            <div key={semana.numero ?? i} className="border border-[#E2E8F0] rounded-lg p-4" data-testid={`week-edit-${i}`}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-[#475569] font-['Montserrat'] mb-1">Nº Semana</label>
                  <input type="number" value={semana.numero || ""} onChange={e => updateWeek(i, "numero", e.target.value)} data-testid={`week-number-${i}`}
                    className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] font-['Montserrat'] mb-1">Fecha inicio</label>
                  <input value={semana.fecha_inicio || ""} onChange={e => updateWeek(i, "fecha_inicio", e.target.value)} data-testid={`week-start-${i}`}
                    className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]" placeholder="22 de Junio" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] font-['Montserrat'] mb-1">Fecha fin</label>
                  <input value={semana.fecha_fin || ""} onChange={e => updateWeek(i, "fecha_fin", e.target.value)} data-testid={`week-end-${i}`}
                    className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]" placeholder="26 de Junio" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] font-['Montserrat'] mb-1">Año</label>
                  <input value={semana.year || ""} onChange={e => updateWeek(i, "year", e.target.value)} data-testid={`week-year-${i}`}
                    className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]" placeholder="2026" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Daily schedule */}
      {dias.length > 0 && (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-6">
          <h3 className="font-['Montserrat'] font-semibold text-[#0F172A] mb-4">Cronograma por Día</h3>
          <div className="flex gap-2 mb-4 flex-wrap">
            {dias.map((dia, i) => (
              <button key={dia.dia} onClick={() => setActiveDay(i)} data-testid={`schedule-day-tab-${dia.dia}`}
                className={`px-3 py-1.5 rounded-full font-['Montserrat'] text-sm font-semibold transition-all ${activeDay === i ? "bg-[#0072A4] text-white" : "bg-[#F0F8FF] text-[#475569] hover:bg-[#E0F0FF]"}`}>
                {dia.dia}
              </button>
            ))}
          </div>
          {dias[activeDay] && (
            <div className="space-y-2" data-testid="schedule-slots">
              <div className="grid grid-cols-3 gap-2 mb-2 text-xs font-['Montserrat'] font-semibold text-[#475569] uppercase tracking-wide px-2">
                <span>Hora</span><span>Grupo 1</span><span>Grupo 2</span>
              </div>
              {dias[activeDay].actividades.map((slot, si) => (
                <div key={`${slot.hora || "slot"}-${si}`} className="grid grid-cols-3 gap-2 items-center" data-testid={`slot-${si}`}>
                  <input value={slot.hora} onChange={e => updateSlot(activeDay, si, "hora", e.target.value)}
                    className="border border-[#E2E8F0] rounded-lg px-2 py-1.5 text-xs font-['Montserrat'] focus:outline-none focus:border-[#0072A4]" placeholder="09:00 - 11:00" />
                  <input value={slot.g1} onChange={e => updateSlot(activeDay, si, "g1", e.target.value)}
                    className="border border-[#E2E8F0] rounded-lg px-2 py-1.5 text-xs font-['Montserrat'] focus:outline-none focus:border-[#0072A4]" placeholder="Actividad G1" />
                  <div className="flex gap-1">
                    <input value={slot.g2} onChange={e => updateSlot(activeDay, si, "g2", e.target.value)}
                      className="flex-1 border border-[#E2E8F0] rounded-lg px-2 py-1.5 text-xs font-['Montserrat'] focus:outline-none focus:border-[#0072A4]" placeholder="Actividad G2" />
                    <button onClick={() => removeSlot(activeDay, si)} className="text-red-400 hover:text-red-600" data-testid={`remove-slot-${si}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
              <button onClick={() => addSlot(activeDay)} data-testid="add-slot-btn"
                className="flex items-center gap-2 text-[#0072A4] font-['Montserrat'] text-sm font-semibold hover:text-[#005A80] transition-colors mt-2">
                <Plus size={14} /> Añadir franja horaria
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
